/**
 * Gathering system: worker state machine over resource nodes.
 *   toResource -> gathering -> toDropoff -> toResource (repeat)
 * Handles node depletion and drop-site deposit into player economy.
 */
import type { World } from "../world";
import type { Registry } from "../../data/registry";
import { FIXED_DT } from "../types";
import { findPath } from "../pathfinding";
import { advanceOrder, repathToDoor, buildingStand } from "../orders";
import { domainOf, canGatherNode } from "../naval";
import type { Gatherer, Move, Transform } from "../components";

type DropSiteFn = (world: World, player: number, x: number, y: number, gatherer?: number) => number | null;

/** Interaction reach from the building reference point. Land paths stop outside
 * the model, so large footprints need their actual contact radii; a dock uses its
 * already-resolved shore/water stand point and keeps the compact local tolerance. */
function dropoffReach(world: World, worker: number, dropSite: number): number {
  const b = world.building.get(dropSite);
  if (b && world.isWater(b.origin.x, b.origin.y)) return world.size * 0.7;
  const wr = world.transform.get(worker)?.radius ?? 0;
  const br = world.transform.get(dropSite)?.radius ?? 0;
  return Math.max(world.size * 0.7, wr + br + world.size * 0.15);
}

function repath(world: World, eid: number, tx: number, ty: number) {
  const tr = world.transform.get(eid);
  const mv = world.move.get(eid);
  if (!tr || !mv) return;
  // a fishing boat (naval) routes over water to its catch; a land worker on land.
  mv.path = findPath(world, tr.pos.x, tr.pos.y, tx, ty, 6000, domainOf(world, eid), world.owner.get(eid)?.player, false, tr.radius) ?? [];
}

function nearestNodeOfType(
  world: World,
  resource: string,
  x: number,
  y: number,
  gatherer: number,
  maxDist = 40
): number | null {
  let best: number | null = null;
  let bestD = maxDist * maxDist;
  for (const [eid, r] of world.resource) {
    if (r.resource !== resource) continue;
    if (!canGatherNode(world, gatherer, eid)) continue; // only nodes this unit can reach (domain)
    const tr = world.transform.get(eid);
    if (!tr) continue;
    const d = (tr.pos.x - x) ** 2 + (tr.pos.y - y) ** 2;
    // tie-break by lower eid: world.resource iteration is not eid-monotone (a
    // carcass re-inserts a low eid late), so an order-dependent pick desyncs.
    if (d < bestD || (d === bestD && eid < (best ?? Infinity))) {
      bestD = d;
      best = eid;
    }
  }
  return best;
}

export function gatheringSystem(world: World, _reg: Registry): void {
  const dropSite = (world as any)._nearestDropSite as DropSiteFn;

  /** Head to the nearest drop-site to unload the current carry. */
  const beginDropoff = (eid: number, g: Gatherer, tr: Transform, owner: number): void => {
    g.state = "toDropoff";
    const ds = dropSite ? dropSite(world, owner, tr.pos.x, tr.pos.y, eid) : null;
    g.dropSite = ds;
    if (ds && world.transform.has(ds)) repathToDoor(world, eid, ds);
  };

  /** Decide what to do once the current resource node is gone. */
  const routeAfterNodeLost = (eid: number, g: Gatherer, mv: Move, tr: Transform, owner: number): void => {
    if (mv.queue.length) {
      const nx = mv.queue[0];
      const sameType = nx.kind === "gather" && world.resource.get(nx.node)?.resource === g.resource;
      // Different resource (or any non-gather follow-up) → bank the partial load
      // FIRST, then the after-deposit handler advances to the queued order. Same
      // resource → just carry on (a partial load deposits when it fills).
      if (g.carrying > 1e-6 && !sameType) { beginDropoff(eid, g, tr, owner); return; }
      advanceOrder(world, eid);
      return;
    }
    // No queue: top up from the nearest same-type node; if none remain, bank any
    // partial load; otherwise idle in place.
    const next = g.resource ? nearestNodeOfType(world, g.resource, tr.pos.x, tr.pos.y, eid) : null;
    if (next) {
      g.node = next;
      g.state = "toResource";
      repath(world, eid, world.transform.get(next)!.pos.x, world.transform.get(next)!.pos.y);
    } else if (g.carrying > 1e-6) {
      beginDropoff(eid, g, tr, owner);
    } else {
      g.state = "toResource";
    }
  };

  for (const [eid, g] of world.gatherer) {
    const mv = world.move.get(eid);
    const tr = world.transform.get(eid);
    const owner = world.owner.get(eid)?.player;
    if (!mv || !tr || owner === undefined) continue;

    // Explicit "return resources" order: walk to the chosen drop-site, deposit,
    // then go fully idle (no auto-resume). Lets a stopped/idle worker that's
    // still carrying be sent to unload.
    if (mv.intent?.kind === "dropoff") {
      const ds = mv.intent.dropSite;
      const dTr = world.transform.get(ds);
      if (!dTr || g.carrying <= 1e-6) {
        g.dropSite = null;
        if (!advanceOrder(world, eid)) mv.intent = undefined;
        continue;
      }
      // Measure to where the worker actually STANDS to deposit (a dock's shore
      // edge, else the building door), not the building centre — a dock sits on
      // water, so a centre-relative radius would never be met from the shore.
      const stand = buildingStand(world, eid, ds) ?? dTr.pos;
      const d = Math.hypot(stand.x - tr.pos.x, stand.y - tr.pos.y);
      // Use model-touch reach for land buildings; the route intentionally stops
      // outside their texture rather than stepping inward to a synthetic door.
      if (d <= dropoffReach(world, eid, ds)) {
        const ps = world.players.get(owner);
        if (ps && g.resource) ps.resources[g.resource] = (ps.resources[g.resource] ?? 0) + g.carrying;
        g.carrying = 0;
        g.dropSite = null;
        g.node = null;
        // honour a shift-queued follow-up (e.g. then gather food); else idle.
        if (!advanceOrder(world, eid)) mv.intent = undefined;
      } else if (mv.path.length === 0) {
        repathToDoor(world, eid, ds);
      }
      continue;
    }

    // Only auto-run the gather loop while the worker's CURRENT order is gather.
    // Any other order — move/attack/build, or idle (undefined intent, e.g. after
    // a move completes or a build finishes) — cancels harvesting. Otherwise a
    // worker sent elsewhere would walk there and then resume gathering the
    // nearest node once its move intent self-cleared on arrival.
    if (mv.intent?.kind !== "gather") continue;

    // validate current node still exists
    if (g.node !== null && !world.resource.has(g.node)) {
      g.node = null;
    }

    if (g.state === "toResource") {
      if (g.node === null) {
        routeAfterNodeLost(eid, g, mv, tr, owner);
        continue;
      }
      const nTr = world.transform.get(g.node);
      const nRes = world.resource.get(g.node);
      if (!nTr || !nRes) {
        g.node = null;
        continue;
      }
      const d = Math.hypot(nTr.pos.x - tr.pos.x, nTr.pos.y - tr.pos.y);
      // Resource hexes are passable, so the worker walks ONTO the resource's
      // own tile (the worker stops at the far edge of this range, so keep it
      // small — ~0.7*size puts it on the tile, right at the tree, not ~half a
      // hex short as the old 1.5*size / fixed-1.4 did).
      if (d <= world.size * 0.7) {
        // arrived: gather
        g.state = "gathering";
        g.timer = 0;
        mv.path = [];
        g.resource = nRes.resource;
      } else if (mv.path.length === 0) {
        repath(world, eid, nTr.pos.x, nTr.pos.y);
      }
    } else if (g.state === "gathering") {
      if (g.node === null) {
        routeAfterNodeLost(eid, g, mv, tr, owner);
        continue;
      }
      const nRes = world.resource.get(g.node);
      if (!nRes) {
        g.node = null;
        continue;
      }
      // Continuous extraction: the carry load grows a little every tick at
      // perGather/gatherTime per second, so the carry bar fills smoothly instead
      // of jumping from 0 to a full perGather chunk.
      const rate = nRes.perGather / nRes.gatherTime; // units/sec
      const take = Math.max(0, Math.min(rate * FIXED_DT, nRes.amount, g.capacity - g.carrying));
      nRes.amount -= take;
      g.carrying += take;
      if (nRes.amount <= 1e-6) {
        world.markDead(g.node);
        g.node = null;
      }
      if (g.node === null) {
        routeAfterNodeLost(eid, g, mv, tr, owner);
        continue;
      }
      if (g.carrying >= g.capacity - 1e-6) beginDropoff(eid, g, tr, owner);
    } else if (g.state === "toDropoff") {
      if (g.dropSite === null || !world.transform.has(g.dropSite)) {
        const ds = dropSite ? dropSite(world, owner, tr.pos.x, tr.pos.y, eid) : null;
        g.dropSite = ds;
        if (!ds) continue;
        if (world.transform.has(ds)) repathToDoor(world, eid, ds);
        continue;
      }
      const dTr = world.transform.get(g.dropSite)!;
      const stand = buildingStand(world, eid, g.dropSite) ?? dTr.pos;
      const d = Math.hypot(stand.x - tr.pos.x, stand.y - tr.pos.y);
      // The continuous route stops at the model edge. Contact reach is derived
      // from both bodies so a large town centre is usable without entering it.
      if (d <= dropoffReach(world, eid, g.dropSite)) {
        // deposit
        const ps = world.players.get(owner);
        if (ps && g.resource && g.carrying > 0) {
          ps.resources[g.resource] = (ps.resources[g.resource] ?? 0) + g.carrying;
        }
        g.carrying = 0;
        // Keep working the SAME node until it's exhausted — a drop-off is just a
        // round trip, NOT order completion. Once the node is gone, route to the
        // next queued order or the nearest same-type node.
        if (g.node && world.resource.has(g.node)) {
          g.state = "toResource";
          repath(world, eid, world.transform.get(g.node)!.pos.x, world.transform.get(g.node)!.pos.y);
        } else {
          routeAfterNodeLost(eid, g, mv, tr, owner);
        }
      } else if (mv.path.length === 0) {
        // re-acquire a continuous route to the door (handles separation nudges
        // that empty the path mid-trip).
        repathToDoor(world, eid, g.dropSite);
      }
    }
  }
}
